import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, CheckCircle, Phone, Zap, BarChart2, ArrowLeft, FileText, Paperclip, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../utils/analytics';

interface ContactForm {
  email: string;
  message: string;
  website: string;
  selectedServices: string[];
}

/** Contrat d'énergie joint (option « Discuter de mon contrat d'énergie ») : PDF, 10 Mo au plus. */
const CONTRACT_MAX_BYTES = 10 * 1024 * 1024;

const readAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const CONTACT_OPTIONS = [
  {
    id: 'call',
    icon: Phone,
    labelKey: 'contact.options.call',
    descKey: 'contact.options.callDesc',
  },
  {
    id: 'quickscan',
    icon: Zap,
    labelKey: 'contact.options.quickscan',
    descKey: 'contact.options.quickscanDesc',
  },
  {
    id: 'demo',
    icon: BarChart2,
    labelKey: 'contact.options.demo',
    descKey: 'contact.options.demoDesc',
  },
  {
    id: 'contract',
    icon: FileText,
    labelKey: 'contact.options.contract',
    descKey: 'contact.options.contractDesc',
  },
];

export default function ContactPage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [contactForm, setContactForm] = useState<ContactForm>({
    email: '',
    message: '',
    website: '',
    selectedServices: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formLoadTime] = useState<number>(Date.now());
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [contractError, setContractError] = useState<string | null>(null);
  const wantsContract = contactForm.selectedServices.includes('contract');

  const pickContract = (file: File | null) => {
    setContractError(null);
    if (!file) { setContractFile(null); return; }
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) { setContractError(t('contact.contractFile.type')); return; }
    if (file.size > CONTRACT_MAX_BYTES) { setContractError(t('contact.contractFile.tooBig')); return; }
    setContractFile(file);
  };

  const toggleOption = (id: string) => {
    setContactForm(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(id)
        ? prev.selectedServices.filter(s => s !== id)
        : [...prev.selectedServices, id],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (contactForm.website) {
      toast.error(t('contact.errors.general'));
      return;
    }

    const timeSinceLoad = Date.now() - formLoadTime;
    if (timeSinceLoad < 3000) {
      toast.error(t('contact.errors.slowDown'));
      return;
    }

    if (!contactForm.email || !contactForm.message) {
      toast.error(t('contact.errors.required'));
      return;
    }

    setSubmitting(true);

    try {
      const formData: Record<string, any> = {
        email: contactForm.email,
        message: contactForm.message,
        submissionTime: timeSinceLoad,
        selectedServices: contactForm.selectedServices,
      };
      if (wantsContract && contractFile) {
        formData.contractFile = await readAsDataUrl(contractFile);
        formData.contractFileName = contractFile.name;
      }

      const functionUrl = `${supabase.supabaseUrl}/functions/v1/send-contact`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabase.supabaseKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: t('contact.errors.general') }));
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || t('contact.errors.general'));
      }

      trackEvent('contact_form_submit', 'engagement', 'success');
      setShowSuccess(true);
      setContactForm({ email: '', message: '', website: '', selectedServices: [] });
      setContractFile(null);

    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.errors.general'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>

        {showSuccess ? (
          <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF5E1' }}>
              <CheckCircle className="w-12 h-12" style={{ color: '#F8BD3D' }} />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900">{t('contact.success.title')}</h2>
            <p className="text-neutral-600 max-w-sm">{t('contact.success.message')}</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-3">
                {t('contact.title')}
              </h1>
              <p className="text-neutral-500 text-base">
                Notre équipe vous répondra dans les plus brefs délais.
              </p>
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="hidden">
                  <input
                    type="text"
                    name="website"
                    value={contactForm.website}
                    onChange={(e) => setContactForm(prev => ({ ...prev, website: e.target.value }))}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-neutral-800">
                    {t('contact.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4" style={{ color: '#F8BD3D' }} />
                    </div>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white text-sm focus:outline-none focus:ring-2"
                      style={{
                        border: '1.5px solid #e5e7eb',
                        color: '#1a1a1a',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={e => (e.target.style.borderColor = '#F8BD3D')}
                      onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
                      placeholder={t('contact.emailPlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3 text-neutral-800">
                    {t('contact.servicesTitle')}
                  </label>
                  <div className="grid grid-cols-1 gap-3">
                    {CONTACT_OPTIONS.map(({ id, icon: Icon, labelKey, descKey }) => {
                      const selected = contactForm.selectedServices.includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleOption(id)}
                          className="flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-200 w-full"
                          style={{
                            border: selected ? '2px solid #F8BD3D' : '1.5px solid #e5e7eb',
                            backgroundColor: selected ? '#FFF9EC' : '#ffffff',
                          }}
                        >
                          <div
                            className="flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: selected ? '#F8BD3D' : '#f3f4f6' }}
                          >
                            <Icon
                              className="w-5 h-5"
                              style={{ color: selected ? '#1a1a1a' : '#6b7280' }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-neutral-900">
                              {t(labelKey)}
                            </p>
                            <p className="text-xs mt-0.5 text-neutral-400">
                              {t(descKey)}
                            </p>
                          </div>
                          <div
                            className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200"
                            style={{
                              borderColor: selected ? '#F8BD3D' : '#d1d5db',
                              backgroundColor: selected ? '#F8BD3D' : 'transparent',
                            }}
                          >
                            {selected && (
                              <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs mt-2 text-neutral-400">
                    {t('contact.servicesOptional')}
                  </p>

                  {wantsContract && (
                    <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: '#FFF9EC', border: '1.5px solid #F8BD3D' }}>
                      <p className="text-sm font-medium text-neutral-800">{t('contact.contractFile.label')}</p>
                      <p className="text-xs text-neutral-500 mt-0.5 mb-3">{t('contact.contractFile.hint')}</p>
                      {contractFile ? (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white border border-neutral-200">
                          <Paperclip className="w-4 h-4 flex-shrink-0" style={{ color: '#F8BD3D' }} />
                          <span className="flex-1 min-w-0 truncate text-sm text-neutral-800">{contractFile.name}</span>
                          <span className="text-xs text-neutral-400">{contractFile.size < 1024 * 1024 ? `${Math.max(1, Math.round(contractFile.size / 1024))} Ko` : `${(contractFile.size / 1024 / 1024).toFixed(1)} Mo`}</span>
                          <button type="button" onClick={() => pickContract(null)} aria-label={t('contact.contractFile.remove')}
                            className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 p-3 rounded-lg bg-white border border-dashed border-neutral-300 hover:border-neutral-400 cursor-pointer text-sm text-neutral-600">
                          <Paperclip className="w-4 h-4" style={{ color: '#F8BD3D' }} />
                          {t('contact.contractFile.button')}
                          <input type="file" accept="application/pdf,.pdf" className="hidden"
                            onChange={e => { pickContract(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                        </label>
                      )}
                      {contractError && <p className="text-xs text-red-500 mt-2">{contractError}</p>}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-neutral-800">
                    {t('contact.message')}
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <MessageSquare className="h-4 w-4" style={{ color: '#F8BD3D' }} />
                    </div>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-white text-sm min-h-[130px] resize-none focus:outline-none"
                      style={{
                        border: '1.5px solid #e5e7eb',
                        color: '#1a1a1a',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={e => (e.target.style.borderColor = '#F8BD3D')}
                      onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
                      placeholder={t('contact.messagePlaceholder')}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#1a1a1a' }}></div>
                      <span>{t('contact.submitting')}</span>
                    </>
                  ) : (
                    t('contact.submit')
                  )}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
